import { UserPageDataRoleItem } from '@maxpreps/apiserviceclients/apiModels';
import { secureOrigin } from '@maxpreps/network/src/origins';
import { setUser } from '@sentry/nextjs';
import { GetServerSidePropsContext } from 'next';
import { parseCookies } from 'nookies';
import { teamAdminPermissionOptions } from '../../constants/authentication';
import DEFAULT_USER_DATA from '../../constants/defaultUserData';
import { IRoleRequirement } from '../../models/IRequiresRoleAndPermission';
import { getReturnUrl } from '../primitiveHelpers/urlHelpers';

export const getUserToken = (context?: GetServerSidePropsContext) =>
  parseCookies(context)['X-MP-UserToken'] || undefined;

const paramsToObject = entries => {
  const result = {};
  // eslint-disable-next-line no-restricted-syntax
  for (const [key, value] of entries) {
    result[key] = value;
  }
  return result;
};

export const getUserDataFromMemberCookie = (
  context?: GetServerSidePropsContext
) => {
  const cookies = parseCookies(context, {
    decode: v => v
  });

  const memberCookie = new URLSearchParams(cookies.MemberName);

  if (!memberCookie.has('id')) {
    return { ...DEFAULT_USER_DATA };
  }

  setUser({
    email: memberCookie.get('email'),
    id: memberCookie.get('id')
  } as any);

  // const userCookie = new URLSearchParams(cookies['mp-mutro']);

  return {
    ...DEFAULT_USER_DATA,
    ...paramsToObject(memberCookie),
    isSignedIn: true
    // isAuthenticated: userCookie?.get('isAuthenticated') === '1' // at time of writing (8/10/20) this is a server only cookie
  };
};

// (Ryan - 2/4/2021) This seems like the wrong place for this
// this looks like team project specific data.
// This has a dependency on /authencation.js but that also looks like team project specific
// imo, both should be moved
export const getPermissionOptionFromList = permissionList => {
  if (!permissionList) {
    return {};
  }

  if (permissionList.find(x => x.name === 'All Access')) {
    return teamAdminPermissionOptions.find(x => x.label === 'All Access');
  }

  if (permissionList.find(x => x.name === 'Data')) {
    return teamAdminPermissionOptions.find(x => x.label === 'Staff');
  }

  if (permissionList.find(x => x.name === 'Communications')) {
    return teamAdminPermissionOptions.find(x => x.label === 'Communications');
  }

  if (permissionList.find(x => x.name === 'Basic')) {
    return teamAdminPermissionOptions.find(x => x.label === 'Basic');
  }

  // Not a supported permission. Return the first permission's name.
  return permissionList[0]
    ? { label: permissionList[0].name, value: permissionList[0].name }
    : {};
};

// Is a user authorized?
// Or stated different does a user meet role and  permission requirements?
// 'has' should be the array retrieved from 'userStore.pageData.roles'
// 'needs' is an array of objects that describe the allowable role + permission combos {role:(name|id), permission: [{(name|id)}], accessId1:id, accessId2:id}
// Only 1 condition in the 'needs' array must be met
// Returns false or the role that meets the criteria (first one)
export const hasPermission = (
  needs: IRoleRequirement[],
  has: UserPageDataRoleItem[]
): boolean => !!getRoleWithPermission(needs, has);

export const getRoleWithPermission = (
  needs: IRoleRequirement[],
  has: UserPageDataRoleItem[]
): UserPageDataRoleItem | undefined => {
  if (!Array.isArray(has)) {
    return undefined;
  }

  let acceptedRole: UserPageDataRoleItem | undefined = undefined;

  // Find the first role in the 'has' array that satifies a requirement in the 'needs' array
  needs.some(requirement =>
    has.some(candidate => {
      // Does role id match?
      if (requirement.roleId.toUpperCase() !== candidate.roleId.toUpperCase()) {
        return false;
      }

      // Are access ids valid for this role?
      // Assume valid if unspecified
      const isAccessId1Valid =
        !requirement.accessId1 || requirement.accessId1 === candidate.accessId1;
      const isAccessId2Valid =
        !requirement.accessId2 || requirement.accessId2 === candidate.accessId2;
      if (!isAccessId1Valid || !isAccessId2Valid) return false;

      // If no specific permissions are required for this role, grant access
      if ((requirement.permissions?.length ?? 0) === 0) {
        acceptedRole = candidate;
        return true;
      }

      // Validate all permissions
      if (requirement.permissions) {
        for (let i = 0; i < requirement.permissions.length; i++) {
          const perm = requirement.permissions[i];
          const userRoleHasPerm = candidate.permissions.find(
            p =>
              p.permissionId.toUpperCase() === perm.permissionId.toUpperCase()
          );
          if (!userRoleHasPerm) return false;
        }
      }

      // All conditions are met
      acceptedRole = candidate;
      return true;
    })
  );

  return acceptedRole;
};

interface IGetAuthenticateURLOptions {
  includeReturnUrl?: boolean;
  nextContext?: GetServerSidePropsContext;
  membershipSource?: string;
}

const DEFAULT_OPTIONS: IGetAuthenticateURLOptions = {
  includeReturnUrl: true,
  membershipSource: 'react_authenticate_default'
};

const tryGetReturnURLFromNextContext = (
  nextContext?: GetServerSidePropsContext
): string | undefined => {
  const referer = nextContext?.req.headers.referer;
  if (referer) return referer;

  const isNextUrl = nextContext?.req?.url?.includes('_next');
  if (!isNextUrl) return nextContext?.req?.url;
};

const getParams = (options: IGetAuthenticateURLOptions): URLSearchParams => {
  const mergedOptions = {
    ...DEFAULT_OPTIONS,
    ...options
  };

  const { includeReturnUrl, nextContext, membershipSource } = mergedOptions;

  const queryParams = new URLSearchParams(`source=${membershipSource}`);

  if (includeReturnUrl) {
    let returnUrl = tryGetReturnURLFromNextContext(nextContext);

    if (returnUrl) {
      returnUrl = getReturnUrl(queryParams, returnUrl);
      queryParams.set('returnurl', returnUrl);
    }
  }

  return queryParams;
};

const REQUEST_PASSWORD_URL = `${secureOrigin}/utility/member/request_password.aspx`;

export const getAuthenticateUrl = options =>
  `${REQUEST_PASSWORD_URL}?${getParams(options).toString()}`;
